import { Injectable } from '@angular/core'
import { ParseService } from 'src/app/parse.service'
import { Objects } from '../../../../../server/src/shared-with-client/object-definitions'
import { UnknownObject } from '../../../../../server/src/shared-with-client/types'
import { SearchBy } from '../../mailing-list/purchase/search-by/search-by.component'
import { ChoozlePixelService } from './choozle-pixel.service'
import { FacebookPixelService } from './facebook-pixel.service'
import { GoogleTagService } from './google-tag.service'
import { LinkedInPixelService } from './linkedin-pixel.service'
import { MicrosoftUetService } from './microsoft-uet.service'
import { PinterestTagService } from './pinterest-tag.service'
import { PosthogService } from './posthog.service'
import { SnapPixelService } from './snap-pixel.service'
import { StackAdaptPixelService } from './stack-adapt-pixel.service'
import { TapfiliateService } from './tapfiliate.service'

type EventType = 'Mailing Order' | 'Mailing List Order'
type EventTypeAbbreviation = 'MO' | 'ML'

type TemplateFilters = 'size' | 'filter' | 'industry'
type Currency = 'USD'

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    constructor(
        private fbPixelService: FacebookPixelService,
        private googleTagService: GoogleTagService,
        private microsoftUetService: MicrosoftUetService,
        private snapPixelService: SnapPixelService,
        private pinterestTagService: PinterestTagService,
        private choozlePixelService: ChoozlePixelService,
        private stackAdaptPixelService: StackAdaptPixelService,
        private linkedInPixelService: LinkedInPixelService,
        private tapfiliateService: TapfiliateService,
        private posthogService: PosthogService,
        private parseService: ParseService
    ) {}

    trackPurchase(establishmentID: string, eventValue: number, eventType: EventType, currency: Currency = 'USD') {
        try {
            const eventTypeMapping: Record<EventType, EventTypeAbbreviation> = {
                'Mailing Order': 'MO',
                'Mailing List Order': 'ML',
            }

            this.tapfiliateService.trackPurchase(eventValue)
            this.fbPixelService.trackPurchase(eventValue).catch((err) => {})
            this.googleTagService.sendPurchaseAndConversionEvent(eventValue, currency, eventType)
            this.microsoftUetService.sendPurchaseEvent(eventValue)
            this.snapPixelService.trackPurchase(eventValue, currency)
            this.pinterestTagService.trackCheckout(eventValue, currency)
            this.choozlePixelService.trackPurchase(eventValue, currency, eventTypeMapping[eventType])
            this.stackAdaptPixelService.trackPurchase(establishmentID, eventValue, eventTypeMapping[eventType])
            this.linkedInPixelService.trackPurchase()
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackPurchase', '')
        }
    }

    trackSignUp() {
        try {
            this.microsoftUetService.sendRegistrationEvent()
            this.snapPixelService.trackSignUp()
            this.pinterestTagService.trackLead()
            this.choozlePixelService.trackSignUp()
            this.stackAdaptPixelService.trackSignUp()
            this.linkedInPixelService.trackSignUp()
            this.fbPixelService.trackCompleteRegistration().catch((err) => {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackSignUp', '')
        }
    }

    trackAddToCart(eventValue: number, mailingOrderId: string) {
        try {
            this.fbPixelService.trackAddToCart(eventValue).catch((err) => {})
            this.pinterestTagService.trackAddToCart().catch((err) => {})
            this.stackAdaptPixelService.trackAddToCart(eventValue, mailingOrderId)
            this.linkedInPixelService.trackAddToCart()
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAddToCart', '')
        }
    }

    trackInitiateCheckout(eventValue: number) {
        try {
            this.fbPixelService.trackInitiateCheckout(eventValue).catch((err) => {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackInitiateCheckout', '')
        }
    }

    trackCustomer(customerId: string) {
        try {
            this.tapfiliateService.trackCustomer(customerId)
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackCustomer', '')
        }
    }

    trackViewContent() {
        try {
            this.fbPixelService.trackViewContent().catch((err) => {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackViewContent', '')
        }
    }

    trackAdvanceEditorLoadFail() {
        try {
            this.posthogService.capture('Advanced Editor Load Failed', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAdvanceEditorLoadFail', '')
        }
    }

    trackAdvanceEditorLoadCompleted() {
        try {
            this.posthogService.capture('Advanced Editor Load Completed', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAdvanceEditorLoadCompleted', '')
        }
    }

    trackProductSelected(productType: Objects.Product.Type.Sign | Objects.Product.Type.BusinessCard | Objects.Product.Code.Any | undefined) {
        try {
            this.posthogService.capture('Product Selected', { property: productType })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackProductSelected', '')
        }
    }

    trackTemplateViewed(templateName: string) {
        try {
            this.posthogService.capture('Template Viewed', { property: templateName })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackTemplateViewed', '')
        }
    }

    trackTemplatesFiltered(filterName: TemplateFilters, productFilter: string) {
        try {
            this.posthogService.capture('Templates Filtered', { [filterName]: productFilter })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackTemplatesFiltered', '')
        }
    }

    trackTemplatesSearched(searchQuery: string) {
        try {
            this.posthogService.capture('Templates Searched', { search_query: searchQuery })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackTemplatesSearched', '')
        }
    }

    trackExpressEditorSavedAsNewTemplate() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Express Editor Saved As New Template', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackExpressEditorSavedAsNewTemplate', '')
        }
    }

    trackExpressEditorProofDownloaded() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Express Editor Proof Downloaded', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackExpressEditorProofDownloaded', '')
        }
    }

    trackExpressEditorEmailProofSent() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Express Editor Email Proof Sent', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackExpressEditorEmailProofSent', '')
        }
    }

    trackExpressEditorNextClicked() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Express Editor Next Clicked', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackExpressEditorNextClicked', '')
        }
    }

    trackExpressEditorSavedChanges() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Express Editor Saved Changes', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackExpressEditorSavedChanges', '')
        }
    }

    trackAdvancedEditorClicked() {
        try {
            this.posthogService.capture('Advanced Editor Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAdvancedEditorClicked', '')
        }
    }

    trackAdvancedEditorPreviewDownloaded() {
        try {
            this.posthogService.capture('Advanced Editor Preview Downloaded', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAdvancedEditorPreviewDownloaded', '')
        }
    }

    trackAdvancedEditorSaved() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Advanced Editor Saved', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAdvancedEditorSaved', '')
        }
    }

    trackAdvancedEditorExit() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Advanced Editor Exited', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAdvancedEditorExit', '')
        }
    }

    trackAdvancedEditorSaveAndExit() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Advanced Editor Saved And Exited', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAdvancedEditorSaveAndExit', '')
        }
    }

    trackAddMailingListProSubscriptionLinkClicked(searchedBy: SearchBy) {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Mailing List Pro Subscription Link Clicked', { searched_by: searchedBy, time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackAddMailingListProSubscriptionLinkClicked', '')
        }
    }

    trackMailingListPurchased() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Mailing List Purchased', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackMailingListPurchased', '')
        }
    }

    trackMailingListOptionsSelected(searchType: string, recordLimit: number) {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Mailing List Options Selected', { search_type: searchType, record_limit: recordLimit, time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackMailingListOptionsSelected', '')
        }
    }

    trackMailingListProSubscriptionPurchased(subscriptionType: string) {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Mailing List Pro Subscription Purchased', { subscription_type: subscriptionType, time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackMailingListProSubscriptionPurchased', '')
        }
    }

    trackBillingSubscriptionSlider() {
        try {
            this.posthogService.capture('Billing Subscription Slider Slid', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackBillingSubscriptionSlider', '')
        }
    }

    trackBillingFiveCentsAdded() {
        try {
            this.posthogService.capture('Billing Subscription Five Cents Added', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackBillingFiveCentsAdded', '')
        }
    }

    trackBillingFiveCentsRemoved() {
        try {
            this.posthogService.capture('Billing Subscription Five Cents Removed', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackBillingFiveCentsRemoved', '')
        }
    }

    trackBillingSubscriptionDropOff(stepName: string) {
        try {
            this.posthogService.capture('Billing Subscription Dropped Off', { step_name: stepName })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackBillingSubscriptionDropOff', '')
        }
    }

    trackBillingSubscriptionPurchased(subscriptionType: string) {
        try {
            const time = this.posthogService.trackTime()

            this.posthogService.capture('Billing Subscription Purchased', { subscription_type: subscriptionType, time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackBillingSubscriptionPurchased', '')
        }
    }

    trackUploadListAutoUploaded() {
        try {
            this.posthogService.capture('Upload List Auto Uploaded', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackUploadListAutoUploaded', '')
        }
    }

    trackUploadListAddUnmappedFields() {
        try {
            this.posthogService.capture('Upload List Added Unmapped Fields', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackUploadListAddUnmappedFields', '')
        }
    }

    trackUploadListIgnoreUnmappedFields() {
        try {
            this.posthogService.capture('Upload List Ignored Unmapped Fields', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackUploadListIgnoreUnmappedFields', '')
        }
    }

    trackUploadListDeleted() {
        try {
            this.posthogService.capture('Upload List Deleted', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackUploadListDeleted', '')
        }
    }

    trackUploadListSaved() {
        try {
            const time = this.posthogService.trackTime()
            this.posthogService.capture('Upload List Saved', { time_spent: time })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackUploadListSaved', '')
        }
    }

    trackUploadListUpdateExistingList() {
        try {
            this.posthogService.capture('Upload List Update Existing List', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackUploadListUpdateExistingList', '')
        }
    }

    trackFreePostcardsCopiedReferralCode() {
        try {
            this.posthogService.capture('Free Postcards Copied Referral Code', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackFreePostcardsCopiedReferralCode', '')
        }
    }

    trackFreePostcardsShowEmailTemplateClicked() {
        try {
            this.posthogService.capture('Free Postcards Show Email Template Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackFreePostcardsShowEmailTemplateClicked', '')
        }
    }
    trackSuppressionListAdded() {
        try {
            this.posthogService.capture('Suppression List Added', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackSuppressionListAdded', '')
        }
    }

    trackSuppressionListRowAdded() {
        try {
            this.posthogService.capture('Suppression List Row Added', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackSuppressionListRowAdded', '')
        }
    }

    trackSuppressionListRowDeleted() {
        try {
            this.posthogService.capture('Suppression List Row Deleted', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackSuppressionListRowDeleted', '')
        }
    }

    trackSuppressionListReplaced() {
        try {
            this.posthogService.capture('Suppression List Replaced', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackSuppressionListReplaced', '')
        }
    }

    trackOrderManagementCreateSimilarClicked() {
        try {
            this.posthogService.capture('Order Management Create Similar Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOrderManagementCreateSimilarClicked', '')
        }
    }

    trackOrderPurchaseMailingListClicked() {
        try {
            this.posthogService.capture('Order Summary Purchase Mailing List Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOrderPurchaseMailingListClicked', '')
        }
    }

    trackOrderSummarySavedChanges() {
        try {
            this.posthogService.capture('Order Summary Save Changes Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOrderSummarySavedChanges', '')
        }
    }

    trackOrderSummaryBackClicked() {
        try {
            this.posthogService.capture('Order Summary Back Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOrderSummaryBackClicked', '')
        }
    }

    trackOrderSummaryCheckoutCompleted() {
        try {
            this.posthogService.capture('Order Summary Checkout Completed', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOrderSummaryCheckoutCompleted', '')
        }
    }

    trackManageMailingListCreatedFacebookAudienceClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Create Facebook Audience Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListCreatedFacebookAudienceClicked', '')
        }
    }

    trackManageMailingListUploadClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Upload Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListUploadClicked', '')
        }
    }

    trackManageMailingListPurchaseClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Purchase Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListPurchaseClicked', '')
        }
    }

    trackManageMailingListAddMyAddressClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Add My Address Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListAddMyAddressClicked', '')
        }
    }

    trackManageMailingListAddAddressClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Add Address Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListAddAddressClicked', '')
        }
    }

    trackManageMailingListRefreshClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Refresh Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListRefreshClicked', '')
        }
    }

    trackManageMailingListViewMappedCSVClicked() {
        try {
            this.posthogService.capture('Manage Mailing View Mapped CSV Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListViewedMappedCSVClicked', '')
        }
    }

    trackManageMailingListViewOriginalCSVClicked() {
        try {
            this.posthogService.capture('Manage Mailing Viewed Original CSV Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListViewedOriginalCSVClicked', '')
        }
    }

    trackManageMailingListRunAgainstSuppressionListClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Run Against Suppression List Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListRanAgainstSuppressionListClicked', '')
        }
    }

    trackManageMailingListRunDuplicateDetectionClicked() {
        try {
            this.posthogService.capture('Manage Mailing List Run Duplicate Detection Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackManageMailingListRanDuplicateDetectionClicked', '')
        }
    }

    trackNavbarSubscribeAndSave() {
        try {
            this.posthogService.capture('Navbar Subscribe And Save Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackNavbarSubscribeAndSave', '')
        }
    }

    trackUserPilotBannerClicked(currentPage: string, extras?: UnknownObject) {
        try {
            this.posthogService.capture('User Pilot Banner Clicked', { currentPage, ...extras })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackUserPilotBannerClicked', '')
        }
    }

    trackOnboardingMlsUserSelected() {
        try {
            this.posthogService.capture('Onboarding Mls User Selected', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOnboardingMlsUserSelected', '')
        }
    }

    trackOnboardingSkipMlsUserClicked() {
        try {
            this.posthogService.capture('Skip Onboarding Connect Mls User Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOnboardingSkipMlsUserClicked', '')
        }
    }

    getFreePostcardSampleClicked() {
        try {
            this.posthogService.capture('Onboarding Get Free Postcard Sample Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.getFreePostcardSampleClicked', '')
        }
    }

    skipFreePostcardSampleClicked() {
        try {
            this.posthogService.capture('Onboarding Skip Free Postcard Sample Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.skipFreePostcardSampleClicked', '')
        }
    }

    trackConfirmDesignProfileDetailsClicked() {
        try {
            this.posthogService.capture('Onboarding Confirm Design Profile Details Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackConfirmAgentDetailsClicked', '')
        }
    }

    trackConfirmDesignProfileDetailsBackButtonClicked() {
        try {
            this.posthogService.capture('Onboarding Confirm Design Profile Details Back Button Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackConfirmAgentDetailsBackButtonClicked', '')
        }
    }

    trackOnboardingDroppedOff(stepName: string) {
        try {
            this.posthogService.capture('Onboarding Dropped Off', { step_name: stepName })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOnboardingDroppedOff', '')
        }
    }
    trackShopSubscriptionNowClicked() {
        try {
            this.posthogService.capture('Mega Menu Shop Subscription Now Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackShopSubscriptionNowClicked', '')
        }
    }

    trackNavbarCreateNewOrder() {
        try {
            const btnLocation = 'top_nav'
            this.posthogService.capture('Navbar Create New Order Clicked', { btn_location: btnLocation })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackNavbarCreateNewOrder', '')
        }
    }

    trackTemplatesFiltersSelected(filters: Record<string, string>, isSeeAll: boolean) {
        try {
            this.posthogService.capture('Templates Filters Selected', { filters, isSeeAll })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackTemplatesFiltersSelected', '')
        }
    }

    trackOnboardingLetsGetStarted() {
        try {
            this.posthogService.capture("Onboarding Let's Get Started Clicked", {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOnboardingLetsGetStarted', '')
        }
    }

    trackOnboardingRealStateAgentNextClick() {
        try {
            this.posthogService.capture('Real Estate Agent Onboarding Next Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOnboardingRealStateAgentNextClick', '')
        }
    }

    trackOnboardingSuccessPageUpgradeNowClicked() {
        try {
            this.posthogService.capture('Onboarding Success Page Upgrade Now Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOnboardingSuccessPageUpgradeNowClicked', '')
        }
    }

    trackOnboardingSuccessPageUpgradeLaterClicked() {
        try {
            this.posthogService.capture('Onboarding Success Page Upgrade Later Clicked', {})
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackOnboardingSuccessPageUpgradeLaterClicked', '')
        }
    }

    trackTemplatePageProductPanel(productCode: Objects.Product.Code.Any) {
        try {
            this.posthogService.capture('Templates Page Left Panel Product Selected', { product: productCode })
        } catch (err) {
            this.parseService.reportUIError(err, 'AnalyticsService.trackTemplatePageProductPanel', '')
        }
    }
}
